/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback } from "react";
import { isFunction } from "@adapptio/lib-shared";
import { contextMenuManager } from "../../contextMenu/ContextMenuManager";
import { TContextMenuId, TGetContextMenuParams } from "../../contextMenu/contextMenuRegister";

export interface IUseContextMenu<MenuId extends TContextMenuId = TContextMenuId> {
	showMenu: (event: React.MouseEvent, params?: TGetContextMenuParams<MenuId>) => void;
	handleContextMenuEvent: React.MouseEventHandler;
}

export function useContextMenu<MenuId extends TContextMenuId = TContextMenuId>(
	id: MenuId,
	params?: TGetContextMenuParams<MenuId>
): IUseContextMenu<MenuId> {
	const showMenu = useCallback<IUseContextMenu["showMenu"]>(
		(event, showParams) => {
			contextMenuManager.showMenu({ id, event, params: { ...showParams } });
		},
		[ id ]
	);

	const handleContextMenuEvent = useCallback<IUseContextMenu["handleContextMenuEvent"]>(
		(event) => {
			if (isFunction(event?.preventDefault)) {
				event.preventDefault();
			}

			showMenu(event, params);
		},
		[ showMenu, params ]
	);

	return { showMenu, handleContextMenuEvent };
}
